import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DashboardEmbedToken, DashboardEmbedTokenResponse } from 'app/shared/models/dashboard-embed.model';
import { environment } from 'environments/environment';
import { MonoTypeOperatorFunction, Observable, map, retry, timer } from 'rxjs';

/**
 * Mints the short-lived token that lets the browser render the published Databricks AI/BI dashboard
 * (DATA-1248, `POST /api/dashboard-embed/token` on the same gx data client as the DEO assistant).
 *
 * **The request carries no body, and the UI holds no dashboard id.** Which dashboard a tenant gets
 * is the backend's decision, and it arrives on the response along with the workspace it lives in.
 *
 * **Raw `HttpClient`, not `HttpService`.** `HttpClientWrapper` navigates to `/error` on any 5xx, and
 * 502 is the routine failure here — anything Databricks-side surfaces as one. A dashboard that will
 * not load must leave a hole in its own panel, not throw the user off the route they were on.
 */
@Injectable({
  providedIn: 'root'
})
export class DashboardEmbedService {
  private static readonly SERVICE_UNAVAILABLE_RETRY_COUNT = 2;
  private static readonly SERVICE_UNAVAILABLE_RETRY_DELAY_MS = 2000;

  private readonly _apiUrl = `${environment.backendUrl}/mcp/gxcl/api/dashboard-embed`;

  constructor(private readonly _http: HttpClient) {}

  /**
   * Requests a viewer token, and with it the dashboard to render.
   *
   * Nothing is sent: the tenant is read server-side from `x-gains-tenant-id` (added to every request
   * by `AccessTokenInterceptor`) and bound to the dashboard's per-viewer row filter, so the browser
   * has no say in which rows — or which dashboard — it gets.
   *
   * A `POST` that retries is unusual, and safe here: minting a token creates nothing and changes
   * nothing, so a repeat costs only a second token that is never used.
   */
  getEmbedToken(): Observable<DashboardEmbedToken> {
    return this._http.post<DashboardEmbedTokenResponse>(`${this._apiUrl}/token`, null).pipe(
      this.retryWhenServiceUnavailable(),
      map(response => this.toEmbedToken(response))
    );
  }

  /**
   * `instance_url` and `workspace_id` differ per environment and come from the response for that
   * reason — nothing about the workspace is hardcoded in the UI.
   */
  private toEmbedToken(response: DashboardEmbedTokenResponse): DashboardEmbedToken {
    return {
      token: response.token,
      instanceUrl: response.instance_url,
      workspaceId: response.workspace_id,
      dashboardId: response.dashboard_id
    };
  }

  /**
   * 503 means the embed service is still starting or failed to initialise, which a moment later it
   * has not — so retry a bounded number of times before surfacing it. 422 (missing tenant header)
   * and 502 (anything Databricks-side) are not retryable and rethrow immediately.
   */
  private retryWhenServiceUnavailable<T>(): MonoTypeOperatorFunction<T> {
    return retry({
      count: DashboardEmbedService.SERVICE_UNAVAILABLE_RETRY_COUNT,
      delay: (error: unknown, retryCount: number) => {
        if (error instanceof HttpErrorResponse && error.status === 503) {
          return timer(retryCount * DashboardEmbedService.SERVICE_UNAVAILABLE_RETRY_DELAY_MS);
        }
        throw error;
      }
    });
  }
}
